import Vue from 'vue';
import notificationService from '@/services/notificationService';
import { Module } from 'vuex';
import groupsService from '@/services/graph/groupsService';
import settings from '@/settings.json';
import { SecurityConfig } from "@/installationConfig";
import { EmployeesClient } from '@/data/web-api-client';
import { sortEmployeesByName } from '@/modules/employees/employeeSelection';

const employeesModule: Module<any, any> = {
    namespaced: true,
    state: {
        loading: true,
        initialised: false,
        items: [],
        // OS-214: AAD id sets for the project leader / quality guard role groups. Fetched once
        // (see fetchRoleGroups below) and cached here for the lifetime of the app session - the
        // backend query itself is also cached (see GetEmployeeSelectionGroupsQuery), so this is
        // a second, frontend-side layer of "don't hit O365 on every mount".
        roleGroupsInitialised: false,
        projectLeaderIds: null,
        qualityGuardIds: null,
    },
    getters: {
        items: (state) => state.items,
        itemByUserId: (state) => (id: string) => state.items.find((o: any) => o.id === id),
        loading: (state) => state.loading,
        projectLeaderIds: (state) => state.projectLeaderIds,
        qualityGuardIds: (state) => state.qualityGuardIds,
    },
    mutations: {
        setItems(state, objects) {
            state.initialised = true;
            state.items = objects;
        },
        setLoading(state, value) {
            state.loading = value;
        },
        setSaving(state, value) {
            state.saving = value;
        },
        setRoleGroups(state, { projectLeaderIds, qualityGuardIds }) {
            state.roleGroupsInitialised = true;
            state.projectLeaderIds = projectLeaderIds;
            state.qualityGuardIds = qualityGuardIds;
        },
    },
    actions: {
        async fetch({ dispatch, state }, { axios }) {
            if (!state.initialised) {
                await dispatch('load', { axios });
            }
        },
        async load({ commit, dispatch }, { axios }) {
            commit('setLoading', true);
            try {
                const res: any[] = await new EmployeesClient(undefined, axios).getEmployeesFromO365();
                commit('setItems', sortEmployeesByName(res.filter((x: any) => x.accountEnabled == true)));
            } catch (err) {
                notificationService.failedToLoad();
                await dispatch('load');
            } finally {
                commit('setLoading', false);
            }
        },
        // OS-214: resolves the project leader / quality guard AAD id sets once per app session.
        // Also fails open: if the request itself fails, both sets are set to null so every
        // caller of selectableEmployees()/isSelectableAs...() falls back to "all active
        // employees" instead of getting stuck on an empty picker.
        async fetchRoleGroups({ commit, state }, { axios }) {
            if (state.roleGroupsInitialised) {
                return;
            }
            try {
                const dto = await new EmployeesClient(undefined, axios).getEmployeeSelectionGroups();
                commit('setRoleGroups', {
                    projectLeaderIds: dto.projectLeaderAdUserIds || null,
                    qualityGuardIds: dto.qualityGuardAdUserIds || null,
                });
            } catch (err) {
                commit('setRoleGroups', { projectLeaderIds: null, qualityGuardIds: null });
            }
        },
    },
};

export default employeesModule;
